/* ==========================================================================
   VYORO B2B — Collection filtering and sorting
   Uses Shopify's native Storefront Filtering. Every control writes real filter
   parameters into the URL and the product grid is re-rendered from the
   Section Rendering API, so what is shown always matches what Shopify returns.
   ========================================================================== */

(function () {
  'use strict';

  const VYORO = (window.VYORO = window.VYORO || {});
  const utils = VYORO.utils;

  const container = document.querySelector('[data-collection-section]');
  if (!container) return;

  const sectionId = container.getAttribute('data-section-id');
  let isLoading = false;

  /* ------------------------------------------------------------------------
     URL building
     ---------------------------------------------------------------------- */

  function buildUrlFromForm(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      if (value === '' || value == null) return;
      params.append(key, value);
    });

    const base = form.getAttribute('action') || window.location.pathname;
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  }

  /* ------------------------------------------------------------------------
     Rendering
     ---------------------------------------------------------------------- */

  async function applyUrl(url, options) {
    const settings = options || {};
    if (isLoading) return;
    isLoading = true;

    const grid = container.querySelector('[data-product-grid]');
    if (grid) grid.setAttribute('aria-busy', 'true');

    try {
      const html = await utils.fetchSection(sectionId, url);
      const doc = utils.parseHTML(html);
      const fresh = doc.querySelector('[data-collection-section]');

      if (fresh) {
        container.innerHTML = fresh.innerHTML;

        // Section Rendering API inserts brand-new .reveal elements. The normal
        // page-load observer in theme.js does not see these nodes, so without
        // reactivating them pagination/filter results remain opacity: 0.
        container.querySelectorAll('.reveal, .reveal--stagger').forEach((element) => {
          element.classList.add('is-visible');
        });

        document.querySelectorAll('[data-quantity]').forEach(VYORO.updateQuantityButtons);
      }

      if (!settings.skipHistory) {
        window.history.pushState({ vyoroFilters: url }, '', url);
      }

      // The mobile sheet lives outside the re-rendered container, so keep its
      // inputs in step with the filters that were just applied.
      syncSheetFromUrl(url);

      if (settings.scroll !== false) {
        const target = container.querySelector('[data-results-top]');
        if (target) {
          target.scrollIntoView({
            behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start'
          });
        }
      }

      const count = container.querySelector('[data-result-count]');
      if (count) utils.announce(count.textContent.trim());
    } catch (error) {
      // If re-rendering fails, fall back to a normal navigation.
      window.location.href = url;
    } finally {
      isLoading = false;
      // `grid` points to the pre-render node after a successful Section
      // Rendering API replacement. Always clear the flag from the live grid.
      const activeGrid = container.querySelector('[data-product-grid]');
      if (activeGrid) activeGrid.removeAttribute('aria-busy');
    }
  }

  /* ------------------------------------------------------------------------
     Live count for the mobile sheet, without committing the filters yet
     ---------------------------------------------------------------------- */

  async function previewCount(form) {
    const countTarget = document.querySelector('[data-filter-result-count]');
    const applyButton = document.querySelector('[data-filter-apply]');
    if (!countTarget) return;

    const url = buildUrlFromForm(form);

    try {
      const html = await utils.fetchSection(sectionId, url);
      const doc = utils.parseHTML(html);
      const fresh = doc.querySelector('[data-result-count]');

      if (fresh) {
        countTarget.textContent = fresh.textContent.trim();
        if (applyButton) applyButton.setAttribute('data-target-url', url);
      }
    } catch (error) {
      // Leave the previous count in place — the apply button still works.
    }
  }

  /**
   * Reconcile the mobile filter sheet with a URL's filter parameters.
   * Called after a filter is applied from the desktop sidebar or an active
   * filter chip is removed.
   */
  function syncSheetFromUrl(url) {
    const form = document.querySelector('[data-facets-context="mobile"]');
    if (!form) return;

    let params;
    try {
      params = new URL(url, window.location.origin).searchParams;
    } catch (error) {
      return;
    }

    form.querySelectorAll('[data-facet-input]').forEach((input) => {
      if (input.type === 'checkbox') {
        const values = params.getAll(input.name);
        input.checked = values.indexOf(input.value) !== -1;
      } else {
        input.value = params.get(input.name) || '';
      }
    });

    const sort = form.querySelector('[data-facet-sort]');
    if (sort && params.get('sort_by')) sort.value = params.get('sort_by');

    updateActiveCount(form);

    const countTarget = document.querySelector('[data-filter-result-count]');
    const liveCount = container.querySelector('[data-result-count]');
    if (countTarget && liveCount) countTarget.textContent = liveCount.textContent.trim();

    const applyButton = document.querySelector('[data-filter-apply]');
    if (applyButton) applyButton.removeAttribute('data-target-url');
  }

  function updateActiveCount(form) {
    const badge = document.querySelector('[data-filter-active-count]');
    if (!badge) return;

    const active = form.querySelectorAll('[data-facet-input]:checked').length;
    badge.textContent = active;
    badge.hidden = active === 0;
  }

  /* ------------------------------------------------------------------------
     Events
     ---------------------------------------------------------------------- */

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-facet-input], [data-facet-sort]');
    if (!input) return;

    const form = input.closest('[data-facets-form]');
    if (!form) return;

    const context = form.getAttribute('data-facets-context');

    if (context === 'mobile') {
      // The bottom sheet previews the result count and waits for "Show results".
      updateActiveCount(form);
      previewCount(form);
    } else {
      applyUrl(buildUrlFromForm(form), { scroll: false });
    }
  });

  document.addEventListener('click', (event) => {
    // Apply from the mobile sheet
    const apply = event.target.closest('[data-filter-apply]');
    if (apply) {
      const form = document.querySelector('[data-facets-context="mobile"]');
      const url = apply.getAttribute('data-target-url') || (form ? buildUrlFromForm(form) : null);
      const sheet = VYORO.getOverlay('FilterSheet');
      if (sheet) sheet.close();
      if (url) applyUrl(url);
      return;
    }

    // Reset all filters
    const reset = event.target.closest('[data-filter-reset]');
    if (reset) {
      const form = reset.closest('.filter-sheet').querySelector('[data-facets-form]');
      if (form) {
        form.querySelectorAll('[data-facet-input]').forEach((input) => {
          if (input.type === 'checkbox') {
            input.checked = false;
          } else {
            input.value = '';
          }
        });
        updateActiveCount(form);
        previewCount(form);
      }
      return;
    }

    // Remove a single active filter chip
    const removeChip = event.target.closest('[data-facet-remove]');
    if (removeChip) {
      event.preventDefault();
      applyUrl(removeChip.getAttribute('href'), { scroll: false });
      return;
    }

    // Paginate without a full reload
    const pageLink = event.target.closest('.pagination__item[href]');
    if (pageLink && container.contains(pageLink)) {
      event.preventDefault();
      applyUrl(pageLink.getAttribute('href'));
    }
  });

  window.addEventListener('popstate', () => {
    applyUrl(window.location.href, { skipHistory: true, scroll: false });
  });
})();
